<script module lang="ts">
  import type { AllowedState } from '../content/states.types';
  import type { IconType } from '../icons/icons.types';
  import type { Snippet } from 'svelte';

  export interface Props {
    state?: AllowedState;
    type?: 'button' | 'submit' | 'reset';
    onclick?: () => void;
    ondblclick?: (event: MouseEvent) => void;
    disabled?: boolean;
    /** Seconds it has to be held before `onclick` fires — without, it is a plain button. */
    hold?: number;
    /** A cooldown: until this moment (`Date.now()`) the full background empties to the left. */
    cooldownUntil?: number;
    /** The whole cooldown in seconds, of which `cooldownUntil` is the end: one already running starts part way. */
    cooldownSeconds?: number;
    /** The seconds left, shown in the bottom right corner. */
    cooldownLeft?: number;
    /** The tooltip during the cooldown, it says what is going on. */
    cooldownHint?: string;
    /** An icon before the text, or alone (then `label` names the button). */
    icon?: IconType;
    /** `aria-label` and tooltip of an icon-only button. */
    label?: string;
    /** No background: text and icon in the colour of `state`; hovered, focused or held it looks like a plain button. */
    transparent?: boolean;
    /** Only as wide as its content (page numbers, paginator). */
    compact?: boolean;
    /** The icon filled instead of outlined (a set favorite). */
    filled?: boolean;
    children?: Snippet;
  }
</script>

<script lang="ts">
  import { CommonTexts } from '../../services/CommonTexts';
  import { allowedStates } from '../content/states.types';
  import Icon from '../icons/Icon.svelte';
  import Tooltip from '../tooltips/Tooltip.svelte';

  const {
    state: colorState,
    type = 'button',
    onclick,
    ondblclick,
    disabled,
    hold,
    cooldownUntil,
    cooldownSeconds = 0,
    cooldownLeft,
    cooldownHint,
    icon,
    label,
    transparent = false,
    compact = false,
    filled = false,
    children,
  }: Props = $props();

  const DefaultState: AllowedState = 'brand';
  const DisabledState = 'disabled';
  /** Released earlier than this, it was a click, not an attempt to hold — then the hint shows. */
  const ShortPressMs = 1000;
  const HintMs = 2500;
  const DoubleClickDelayMs = 250;

  const IsDisabled = $derived(disabled === true || (type === 'button' && onclick === undefined));
  const UseState = $derived(IsDisabled ? DisabledState : colorState && allowedStates.includes(colorState) ? colorState : DefaultState);
  const IsIconOnly = $derived(icon !== undefined && children === undefined);

  // - holding
  const HoldSeconds = $derived(Math.max(0, hold ?? 0));
  const NeedsHold = $derived(HoldSeconds > 0);
  const HintText = $derived(HoldSeconds === 1 ? CommonTexts.HoldHintOne : CommonTexts.format(CommonTexts.HoldHint, { seconds: HoldSeconds }));
  const IsCooling = $derived(cooldownUntil !== undefined);
  const CooldownLeftMs = $derived(cooldownUntil === undefined ? 0 : Math.max(0, cooldownUntil - Date.now()));
  let IsHolding = $state(false);
  let ShowHint = $state(false);
  let HoldStart = 0;
  let HoldTimer: number | undefined;
  /** The hold fired: the click of the release must not count, even if the button needs no hold any more (a released favorite). */
  let IsReleaseClick = false;
  let HintTimer: number | undefined;
  let ClickTimer: number | undefined;
  let ButtonElement: HTMLButtonElement | undefined = $state();

  function startHold(): void {
    if (IsDisabled || !NeedsHold || IsHolding) {
      return;
    }
    IsHolding = true;
    ShowHint = false;
    HoldStart = Date.now();
    HoldTimer = window.setTimeout((): void => {
      HoldTimer = undefined;
      IsHolding = false;
      IsReleaseClick = true;
      onclick?.();
    }, HoldSeconds * 1000);
  }

  /** Released or left before the time is up: nothing fires; a short press gets the hint. */
  function stopHold(): void {
    if (!IsHolding) {
      return;
    }
    window.clearTimeout(HoldTimer);
    HoldTimer = undefined;
    IsHolding = false;
    if (Date.now() - HoldStart < ShortPressMs) {
      showHint();
    }
  }

  function showHint(): void {
    window.clearTimeout(HintTimer);
    ShowHint = true;
    HintTimer = window.setTimeout((): void => {
      ShowHint = false;
    }, HintMs);
  }

  function handleClick(event: MouseEvent): void {
    if (IsReleaseClick) {
      IsReleaseClick = false;
      event.preventDefault();
      return;
    }
    if (NeedsHold) {
      // - a held button fires from its timer, never from the click that follows the release
      event.preventDefault();
      return;
    }

    if (ondblclick) {
      if (ClickTimer !== undefined || event.detail >= 2) {
        window.clearTimeout(ClickTimer);
        ClickTimer = undefined;
        return;
      }
      ClickTimer = window.setTimeout((): void => {
        ClickTimer = undefined;
        onclick?.();
      }, DoubleClickDelayMs);
      return;
    }

    onclick?.();
  }

  function handleDblClick(event: MouseEvent): void {
    if (ClickTimer !== undefined) {
      window.clearTimeout(ClickTimer);
      ClickTimer = undefined;
    }
    ondblclick?.(event);
  }

  function handlePointerDown(event: PointerEvent): void {
    IsReleaseClick = false;
    if (event.button === 0) {
      startHold();
    }
  }

  function handleKeyDown(event: KeyboardEvent): void {
    IsReleaseClick = false;
    if (!NeedsHold || (event.key !== 'Enter' && event.key !== ' ')) {
      return;
    }
    event.preventDefault();
    if (!event.repeat) {
      startHold();
    }
  }

  function handleKeyUp(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      stopHold();
    }
  }

  $effect(() => {
    return (): void => {
      window.clearTimeout(HoldTimer);
      window.clearTimeout(HintTimer);
      window.clearTimeout(ClickTimer);
    };
  });
</script>

<span class="formButton">
  <Tooltip
    placement="top"
    visible={ShowHint ? true : undefined}
    triggerOnHover={(IsIconOnly && Boolean(label)) || (IsCooling && cooldownHint !== undefined)}
    state={ShowHint ? 'notice' : undefined}
    text={ShowHint ? HintText : IsCooling && cooldownHint !== undefined ? cooldownHint : IsIconOnly ? label : undefined}>
    <button
      bind:this={ButtonElement}
      {type}
      class="tagButton"
      class:holding={IsHolding}
      class:cooling={IsCooling}
      class:iconOnly={IsIconOnly}
      class:transparent
      class:compact
      class:filled
      disabled={IsDisabled}
      aria-label={IsIconOnly ? label : undefined}
      style:--button-color={`var(--color-${UseState}-dominant)`}
      style:--button-color-hover={`var(--color-${UseState}-secondary)`}
      style:--button-color-progress={`var(--color-${UseState}-accent)`}
      style:--button-color-front={UseState === 'gray' ? `var(--layout-front)` : `var(--color-${UseState}-accent)`}
      style:--button-hold-duration={`${HoldSeconds.toString()}s`}
      style:--button-cooldown-duration={`${cooldownSeconds.toString()}s`}
      style:--button-cooldown-delay={`${(CooldownLeftMs - cooldownSeconds * 1000).toString()}ms`}
      onclick={handleClick}
      ondblclick={handleDblClick}
      onpointerdown={handlePointerDown}
      onpointerup={stopHold}
      onpointerleave={stopHold}
      onpointercancel={stopHold}
      onkeydown={handleKeyDown}
      onkeyup={handleKeyUp}>
      {#if NeedsHold}
        <span class="buttonProgress" aria-hidden="true"></span>
      {/if}
      {#if IsCooling}
        <span class="buttonProgress buttonCooldown" aria-hidden="true"></span>
        {#if cooldownLeft !== undefined}
          <span class="buttonCounter mono" aria-hidden="true">{cooldownLeft}</span>
        {/if}
      {/if}
      <span class="buttonLabel">
        {#if icon}
          <Icon {icon} size={1.15} />
        {/if}
        {#if children}
          {@render children()}
        {:else if !icon}
          {CommonTexts.ButtonFallback}
        {/if}
      </span>
    </button>
  </Tooltip>
</span>

<style lang="scss">
  @use '../../../styles/display';
  @use '../../../styles/mixins';

  .filled :global(svg) {
    fill: currentcolor;
  }

  .formButton {
    position: relative;
    display: inline-flex;
  }

  .tagButton {
    @include display.inline-block;
    position: relative;
    overflow: hidden;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: calc(var(--font-size) * 16);
    padding: 0.6em 1em;
    font-size: var(--font-size);
    line-height: 1.25;
    text-align: center;
    cursor: pointer;
    border-radius: calc(var(--border-radius) / 2);
    border: 1px solid rgb(from var(--button-color) r g b / 45%);
    background-color: var(--button-color);
    color: var(--layout-font-color);
    user-select: none;
    touch-action: manipulation;

    &:hover,
    &:focus-visible {
      background-color: var(--button-color-hover);
    }

    &.iconOnly {
      min-width: 0;
      padding: 0.15em;

      @include mixins.greaterMedium {
        padding: 0.2em;
      }
    }

    &.compact {
      min-width: 2.25em;
      padding: 0.3em 0.5em;
    }

    // - the fill while held: from the left to the full width in exactly the hold time
    .buttonProgress {
      position: absolute;
      inset: 0 auto 0 0;
      z-index: 0;
      width: 0;
      background: linear-gradient(to right, var(--button-color-hover), var(--button-color-progress));
      transition: width 0.15s linear;
    }

    &.holding .buttonProgress {
      width: 100%;
      transition-duration: var(--button-hold-duration);
    }

    // - the hold fill the other way round; the negative delay lets a cooldown already running start part way
    .buttonCooldown {
      animation: buttonCooldown var(--button-cooldown-duration) linear var(--button-cooldown-delay) forwards;
    }

    @keyframes buttonCooldown {
      from {
        width: 100%;
      }

      to {
        width: 0;
      }
    }

    .buttonCounter {
      position: absolute;
      z-index: 2;
      right: 0.3em;
      bottom: 0.1em;
      font-size: 0.65em;
      line-height: 1.2;
    }

    // - a disabled button swallows the pointer in some browsers: the hover must reach the tooltip around it
    &.cooling {
      pointer-events: none;
    }

    .buttonLabel {
      position: relative;
      z-index: 1;
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 0.4em;
    }

    &.transparent {
      border-color: transparent;
      background-color: transparent;
      color: var(--button-color-front);

      &:hover,
      &:focus-visible,
      &.holding {
        border-color: rgb(from var(--button-color) r g b / 45%);
        background-color: var(--button-color);
        color: var(--layout-font-color);
      }
    }

    &[disabled] {
      cursor: default;

      &:hover,
      &:focus-visible {
        background-color: var(--button-color);
      }

      &.transparent:hover,
      &.transparent:focus-visible {
        border-color: transparent;
        background-color: transparent;
        color: var(--button-color-front);
      }
    }
  }
</style>
